"use client";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import JoditEditor from "jodit-react";
import { useNavigate, useParams } from "react-router-dom";
import { FiCamera, FiMapPin, FiInfo, FiLayers, FiX, FiChevronLeft, FiPlus, FiBriefcase, FiCheckCircle } from "react-icons/fi";

const joditConfig = {
    readonly: false,
    height: 300,
    placeholder: "Write property description here...",
    buttons: ["bold", "italic", "underline", "|", "ul", "ol", "|", "link", "|", "undo", "redo", "clean"],
};

const residentialTypes = ["apartment", "townhouse", "villa-compound", "land", "building", "villa", "penthouse", "hotel-apartment", "floor", "studio"];
const commercialTypes = ["office", "warehouse", "industrial-land", "showroom", "shop", "labour-camp", "bulk-unit", "factory", "mixed-use-land", "other-commercial", "floor", "building", "villa"];
const furnishingOptions = ["furnished", "unfurnished", "semi-furnished"];
const amenityOptions = [
    "Swimming Pool",
    "Gym",
    "Fitness Center",
    "Yoga Studio",
    "Sauna",
    "Spa",
    "Parking",
    "Covered Parking",
    "EV Charging Station",
    "Lift/Elevator",
    "Power Backup",
    "Security",
    "24/7 Security",
    "Controlled Access/Gated",
    "CCTV Surveillance",
    "Garden",
    "Landscaped Gardens",
    "BBQ/Picnic Area",
    "Playground",
    "Children's Play Area",
    "Clubhouse",
    "Community Hall",
    "Business Center",
    "Conference Room",
    "Library",
    "Theater Room",
    "Game Room",
    "Tennis Court",
    "Basketball Court",
    "Jogging Track",
    "Laundry Room",
    "In-Unit Laundry",
    "High-Speed Internet",
    "Wi-Fi Included",
    "On-Site Maintenance",
    "Package Lockers",
    "Bike Storage",
    "Storage Units",
    "Roof Deck/Terrace",
    "Concierge Service",
    "Pet-Friendly (Dog Park)",
    "Non-Smoking Building",
    "Wheelchair Accessible",
    "Air Conditioning",
    "Central Heating",
    "Balcony/Patio",
    "Walk-in Closet",
    "Dishwasher",
    "Microwave",
    "Stainless Steel Appliances",
    "Garbage Disposal",
];

export default function UpdatePropertyPage() {
    const router = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [cityList, setCityList] = useState([]);
    const [availableAreas, setAvailableAreas] = useState([]);
    const [uploadedPhotos, setUploadedPhotos] = useState([]);
    const [description, setDescription] = useState("");
    const [amenities, setAmenities] = useState([]);
    const [aroundProject, setAroundProject] = useState([{ name: "", details: "" }]);

    const [form, setForm] = useState({
        property: "",
        propertyType: "",
        listingCategory: "",
        city: "",
        localityArea: "",
        price: "",
        area: "",
        bedRoom: "",
        bathrooms: "",
        furnishing: "",
        propertyAddress: "",
        permitNo: "",
        rera: "",
        ded: "",
        brn: "",
        phone: "",
        aveneuOverView: {
            projectArea: "",
            size: "",
            projectSize: "",
            launchDate: "",
            possessionStart: "",
        }
    });

    useEffect(() => {
        async function fetchProperty() {
            try {
                const res = await makeRequestAuth("post", "/admin/get-property-by-id", { id });
                if (res?.error) return showErrorToast("Property not found");
                const p = res.data;
                setForm(p);
                setUploadedPhotos(p.uploadedPhotos || []);
                setDescription(p.description || "");
                setAmenities(p.amenities || []);
                setAroundProject(p.aroundProject?.length ? p.aroundProject : [{ name: "", details: "" }]);
            } catch {
                showErrorToast("Failed to load property");
            } finally {
                setLoading(false);
            }
        }
        fetchProperty();
    }, [id]);

    useEffect(() => {
        makeRequestAuth("post", "/user/getCityList").then(res => {
            if (!res?.error) {
                setCityList(res.data || []);
                if (form.city) {
                    const selectedCityData = res.data.find(c => c.cityName === form.city);
                    if (selectedCityData) setAvailableAreas(selectedCityData.areas || []);
                }
            }
        });
    }, [form.city]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleOverviewChange = (e) => {
        setForm({
            ...form,
            aveneuOverView: { ...form.aveneuOverView, [e.target.name]: e.target.value }
        });
    };

    const handleCityChange = (e) => {
        const cityName = e.target.value;
        setForm({ ...form, city: cityName, localityArea: "" });
        const selectedCityData = cityList.find(c => c.cityName === cityName);
        setAvailableAreas(selectedCityData ? selectedCityData.areas : []);
    };

    const handleAroundChange = (index, field, value) => {
        const updated = [...aroundProject];
        updated[index][field] = value;
        setAroundProject(updated);
    };

    const addAroundProject = () => setAroundProject([...aroundProject, { name: "", details: "" }]);
    const removeAroundProject = (index) => setAroundProject(aroundProject.filter((_, i) => i !== index));

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        const { data, error } = await uploadFiles({ files, endpoint: '/uploadMultipleImages' });
        if (error) return showErrorToast("Upload failed");
        setUploadedPhotos(prev => [...prev, ...data.map(i => i.imageUrl)]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanAroundProject = (aroundProject || [])
            .filter(i => i.name && i.details)
            .map(i => ({
                name: i.name.trim(),
                details: i.details.trim(),
            }));

        const cleanAmenities = amenities.map(a => a.trim());

        const payload = {
            id: id,

            property: form.property,
            propertyType: form.propertyType,
            listingCategory: form.listingCategory,

            city: form.city,
            localityArea: form.localityArea,

            price: String(form.price),
            area: String(form.area),
            bedRoom: String(form.bedRoom),
            bathrooms: String(form.bathrooms),

            furnishing: form.furnishing,

            permitNo: form.permitNo?.trim(),
            rera: form.rera?.trim(),
            ded: form.ded?.trim() || "",
            brn: form.brn?.trim() || "",

            phone: form.phone?.trim(),

            propertyAddress: form.propertyAddress?.trim(),

            amenities: cleanAmenities,

            aroundProject: cleanAroundProject,

            description: description?.trim(),

            aveneuOverView: {
                projectArea: form.aveneuOverView?.projectArea?.trim(),
                size: form.aveneuOverView?.size?.trim(),
                projectSize: form.aveneuOverView?.projectSize?.trim(),
                launchDate: form.aveneuOverView?.launchDate?.trim(),
                possessionStart: form.aveneuOverView?.possessionStart?.trim(),
            },

            isBroker: form.isBroker || "no", // Required by backend
            uploadedPhotos: uploadedPhotos,
        };
        const res = await makeRequestAuth("post", "/admin/updateProperty", payload);
        if (!res?.error) {
            showSuccessToast("Property updated successfully");
            setTimeout(() => router(-1), 1500);
        } else {
            showErrorToast(res.message);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-screen animate-pulse text-[#24ADD7]">Loading...</div>;

    return (
        <div className="bg-gray-50 min-h-screen">
            <ToastContainer />

            <div className="max-w-6xl mx-auto p-4 mt-4">
                <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 space-y-10">

                    {/* 1. BASIC INFO */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiLayers /> BASIC INFORMATION
                        </h3>
                        <div className="grid md:grid-cols-3 gap-5">
                            <select className="input" name="property" value={form.property} onChange={handleChange}>
                                <option value="">Select Property</option>
                                <option value="residential">Residential</option>
                                <option value="commercial">Commercial</option>
                            </select>
                            <select className="input" name="propertyType" value={form.propertyType} onChange={handleChange} disabled={!form.property}>
                                <option value="">Property Type</option>
                                {(form.property === "residential" ? residentialTypes : commercialTypes).map(t => (
                                    <option key={t} value={t}>{t.toUpperCase()}</option>
                                ))}
                            </select>
                            <select className="input" name="listingCategory" value={form.listingCategory} onChange={handleChange}>
                                <option value="">Listing Category</option>
                                <option value="buy">Sell</option>
                                <option value="rent">Rent</option>
                            </select>
                            <input className="input" type="number" name="price" placeholder="Price" value={form.price} onChange={handleChange} />
                            <input className="input" type="number" name="area" placeholder="Area (sqft)" value={form.area} onChange={handleChange} />
                            <input className="input" type="number" name="bedRoom" placeholder="Bedrooms" value={form.bedRoom} onChange={handleChange} />
                            <input className="input" type="number" name="bathrooms" placeholder="Bathrooms" value={form.bathrooms} onChange={handleChange} />
                            <select className="input" name="furnishing" value={form.furnishing} onChange={handleChange}>
                                <option value="">Furnishing Status</option>
                                {furnishingOptions.map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
                            </select>
                        </div>
                    </section>

                    {/* 2. LOCATION */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiMapPin /> LOCATION & ADDRESS
                        </h3>
                        <div className="grid md:grid-cols-2 gap-5 mb-5">
                            <select className="input" name="city" value={form.city} onChange={handleCityChange}>
                                <option value="">Select City</option>
                                {cityList.map(c => <option key={c._id} value={c.cityName}>{c.cityName}</option>)}
                            </select>
                            <select className="input" name="localityArea" value={form.localityArea} onChange={handleChange} disabled={!form.city}>
                                <option value="">Select Locality</option>
                                {availableAreas.map((a, i) => <option key={i} value={a}>{a}</option>)}
                            </select>
                        </div>
                        <textarea className="input min-h-[100px]" name="propertyAddress" placeholder="Full Address" value={form.propertyAddress} onChange={handleChange}></textarea>
                    </section>

                    {/* 3. LEGAL INFO */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiCheckCircle /> LEGAL DETAILS
                        </h3>
                        <div className="grid md:grid-cols-4 gap-5">
                            <input className="input" placeholder="Permit No" name="permitNo" value={form.permitNo} onChange={handleChange} />
                            <input className="input" placeholder="RERA No" name="rera" value={form.rera} onChange={handleChange} />
                            <input className="input" placeholder="DED" name="ded" value={form.ded} onChange={handleChange} />
                            <input className="input" placeholder="BRN" name="brn" value={form.brn} onChange={handleChange} />
                        </div>
                    </section>

                    {/* 4. PROJECT OVERVIEW */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiBriefcase /> AVENUE OVERVIEW
                        </h3>
                        <div className="grid md:grid-cols-3 gap-5">
                            <input className="input" placeholder="Project Area (Acres)" name="projectArea" value={form.aveneuOverView?.projectArea} onChange={handleOverviewChange} />
                            <input className="input" placeholder="Unit Sizes" name="size" value={form.aveneuOverView?.size} onChange={handleOverviewChange} />
                            <input className="input" placeholder="Project Size (Units)" name="projectSize" value={form.aveneuOverView?.projectSize} onChange={handleOverviewChange} />
                        </div>
                    </section>

                    {/* 5. AMENITIES */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5">AMENITIES</h3>
                        <select className="input mb-4" onChange={(e) => !amenities.includes(e.target.value) && setAmenities([...amenities, e.target.value])}>
                            <option value="">Add Amenity</option>
                            {amenityOptions.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                        <div className="flex flex-wrap gap-2">
                            {amenities.map(a => (
                                <span key={a} className="bg-orange-50 text-[#24ADD7] border border-orange-200 px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2">
                                    {a} <FiX className="cursor-pointer" onClick={() => setAmenities(amenities.filter(i => i !== a))} />
                                </span>
                            ))}
                        </div>
                    </section>

                    {/* 6. AROUND PROJECT */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5">AROUND PROJECT (POIs)</h3>
                        {aroundProject.map((item, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <input className="input" placeholder="Place Name (e.g. Metro)" value={item.name} onChange={(e) => handleAroundChange(index, "name", e.target.value)} />
                                <div className="flex gap-2">
                                    <input className="input flex-1" placeholder="Distance/Details" value={item.details} onChange={(e) => handleAroundChange(index, "details", e.target.value)} />
                                    <button type="button" onClick={() => removeAroundProject(index)} className="text-[#24ADD7] p-2 hover:bg-blue-100 rounded-lg">×</button>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={addAroundProject} className="text-[#24ADD7] text-sm font-bold mt-2">+ ADD MORE PLACES</button>
                    </section>

                    {/* 7. DESCRIPTION */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiInfo /> PROPERTY DESCRIPTION
                        </h3>
                        <JoditEditor value={description} onBlur={setDescription} config={joditConfig} />
                    </section>

                    {/* 8. PHOTOS */}
                    <section>
                        <h3 className="text-sm font-black text-[#24ADD7] mb-5 flex items-center gap-2">
                            <FiCamera /> PROPERTY PHOTOS
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <label className="border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center h-32 cursor-pointer hover:border-[#24ADD7] transition-all">
                                <FiPlus className="text-gray-400 text-xl" />
                                <input type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" />
                            </label>
                            {uploadedPhotos.map((img, i) => (
                                <div key={i} className="relative h-32 group">
                                    <img src={img} className="h-full w-full object-cover rounded-2xl border" />
                                    <button type="button" onClick={() => setUploadedPhotos(p => p.filter((_, x) => x !== i))} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                        <FiX size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* SUBMIT */}
                    <div className="pt-10 flex flex-col md:flex-row gap-4">
                        <button type="button" onClick={() => router(-1)} className="flex-1 py-4 border-2 border-gray-100 rounded-2xl font-bold text-gray-400 hover:bg-gray-50 transition-all">
                            CANCEL
                        </button>
                        <button type="submit" className="flex-[2] py-4 bg-[#24ADD7] text-white rounded-2xl font-black shadow-lg shadow-blue-300 hover:scale-[1.02] active:scale-[0.98] transition-all">
                            UPDATE PROPERTY NOW
                        </button>
                    </div>
                </form>
            </div>

            <style jsx>{`
                .input {
                    width: 100%;
                    padding: 14px 18px;
                    background-color: #fcfcfc;
                    border: 1px solid #efefef;
                    border-radius: 16px;
                    outline: none;
                    font-size: 0.9rem;
                    color: #333;
                    transition: all 0.3s;
                }
                .input:focus {
                    border-color: #24ADD7;
                    background-color: #fff;
                    box-shadow: 0 0 0 4px rgba(255, 106, 33, 0.05);
                }
            `}</style>
        </div>
    );
}